import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import withWidth from '@material-ui/core/withWidth/withWidth';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography/Typography';
import Error from '@material-ui/icons/Error';
import MainButton from '@configuratorware/configurator-frontendgui/Shared/Components/MainButton';
import CustomDialog from '@configuratorware/configurator-frontendgui/Shared/Components/CustomDialog';
import Tooltip from '@material-ui/core/Tooltip/Tooltip';
import iconCopy2 from '../../Resources/Images/icon-copy-2.svg';
import iconCheckMark2 from '../../Resources/Images/icon-checkmark-2.svg';
import clsx from 'clsx';
import CustomProgress from '../CustomProgress/CustomProgress';
import { useSelector } from 'react-redux';
import { getVariantCode } from '../../Reducers/Configurator/Selectors';

const styles = (theme) => ({
  dialogTitleRoot: {
    padding: '12px 12px 20px',
    fontSize: '1.5rem',
    fontWeight: 400,
  },
  dialogContentRoot: {
    padding: '0 24px 0 12px',
  },
  saveconfigurationDialog: {
    borderTop: '7px solid ' + theme.palette.primary.main,
  },
  activeButton: {
    padding: '6px 15px',
  },
  saveConfigurationCode: {
    marginTop: 24,
    marginBottom: 48,
  },
  saveVariantCode: {
    marginTop: 8,
  },
  TypographyCaption: {
    fontSize: 12,
    lineHeight: 1.7,
  },
  resultRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: 24,
    lineHeight: '28px',
    [theme.breakpoints.down('xs')]: {
      gap: 8,
    },
  },
  successResult: {
    background: '#9CCE00',
  },
  variantResult: {
    background: '#62B841',
  },
  resultFill: {
    flex: '1 1 auto',
    minWidth: 0,
    minHeight: 40,
    color: '#fff',
    padding: '0 8px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    [theme.breakpoints.down('xs')]: {
      minHeight: 56,
      gap: 8,
    },
  },
  successResultIcon: {
    flex: '0 0 auto',
  },
  icon: {
    width: '36px',
    height: '25px',
    padding: '2px',
    minWidth: '0px',
    filter: `invert(1)`,
  },
  copyShareUrlIcon: {
    flex: '0 0 auto',
    '&:hover': {
      color: 'rgba(0,0,0, 0.5)',
      cursor: 'pointer',
    },
  },
  resultLabel: {
    flex: '0 0 180px',
    minWidth: 0,
    color: 'rgba(0, 0, 0, 0.87)',
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '20px',
    textAlign: 'left',
    whiteSpace: 'normal',
    [theme.breakpoints.down('xs')]: {
      flexBasis: 112,
      fontSize: 12,
      lineHeight: '15px',
    },
  },
  resultValue: {
    flex: '1 1 auto',
    minWidth: 0,
    textAlign: 'center',
    fontWeight: 700,
    overflowWrap: 'anywhere',
  },
  resultAction: {
    flex: '0 0 36px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  errorResult: {
    background: '#ED1C29',
    color: '#fff',
    padding: '14px 8px',
    borderRadius: '4px',
    fontSize: 14,
    lineHeight: 1.7,
    textAlign: 'center',
  },
  errorResultIcon: {
    float: 'left',
  },
  dialogSavingInProgress: {
    flex: 1,
    alignItems: 'center',
    flexDirection: 'column',
    display: 'flex',
    height: 50,
    marginTop: 74,
  },
});

interface Props {
  classes: object;
  workInProgress: boolean;
  errorMessage: string;
  code: string;
  loadLoginInfoInProgress: boolean;
  show: boolean;
  onClose: () => void;
  onOkClick: () => void;
  ProgressComponent: object;
  shareUrl: string;
  IconCopyShareUrl: React.ElementType;
}

const CustomSaveConfiguration: React.FC<Props> = (props) => {
  const {
    classes,
    workInProgress,
    errorMessage,
    code,
    loadLoginInfoInProgress,
    show,
    onClose,
    onOkClick,
  } = props;
  const { isFetching } = useSelector((state) => state.api);
  const variantCode = useSelector(getVariantCode);
  const onCopyShareUrl = (code) => {
    try {
      navigator.clipboard.writeText(code);
    } catch (err) {
      // clipboard api only available in secure contexts (localhost, htttps) and modern(ish) browsers
      const textArea = document.createElement('textarea');
      textArea.textContent = code;
      textArea.style.position = 'fixed'; //avoid scrolling to bottom
      document.body.appendChild(textArea);
      const selection = document.getSelection();
      const range = document.createRange();
      range.selectNode(textArea);
      selection.removeAllRanges();
      selection.addRange(range);
      try {
        document.execCommand('copy');
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('Configuration link could not be copied: ', err);
      } finally {
        selection.removeAllRanges();
        document.body.removeChild(textArea);
      }
    }
  };
  return (
    <CustomDialog
      open={show}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      onCancel={onClose}
      dialogTitle={t('saveConfiguration.dialogTitle')}
    >
      <DialogContent className={classes.dialogContentRoot}>
        {' '}
        {workInProgress && (
          <div className={classes.dialogSavingInProgress}>
            <CustomProgress loading={isFetching} />
          </div>
        )}
        {errorMessage && (
          <Typography
            variant="body1"
            classes={{
              root: classes.TypographyCaption,
            }}
            id="alert-dialog-description"
          >
            <div className={classes.errorResult}>
              <Error className={classes.errorResultIcon} />
              {t(errorMessage)}
            </div>
          </Typography>
        )}
        {!workInProgress && code && (
          <React.Fragment>
            <Typography
              variant="body1"
              classes={{ root: classes.TypographyCaption }}
              id="alert-dialog-description"
            >
              {t('saveConfiguration.successfullySaved')}
            </Typography>

            <Typography
              variant="body1"
              classes={{ root: classes.TypographyCaption }}
              id="alert-dialog-description"
            >
              {t('saveConfiguration.infoText')}
            </Typography>
            <Typography
              id="alert-dialog-description"
              variant="h5"
              classes={{ root: classes.saveConfigurationCode }}
            >
              <div className={classes.resultRow}>
                <span className={classes.resultLabel}>{t('saveConfiguration.linetGroupCode')}</span>
                <span className={clsx(classes.resultFill, classes.successResult)}>
                  <img src={iconCheckMark2} className={clsx(classes.successResultIcon, classes.icon)} />
                  <span className={classes.resultValue}>{code}</span>
                  <span className={classes.resultAction}>
                    <Tooltip title={t('saveConfiguration.copyConfigurationLink')}>
                      <img
                        src={iconCopy2}
                        className={clsx(classes.copyShareUrlIcon, classes.icon)}
                        onClick={() => onCopyShareUrl(code)}
                      />
                    </Tooltip>
                  </span>
                </span>
              </div>
              {variantCode && (
                <div className={clsx(classes.resultRow, classes.saveVariantCode)}>
                  <span className={classes.resultLabel}>{t('saveConfiguration.modelNumber')}</span>
                  <span className={clsx(classes.resultFill, classes.variantResult)}>
                    <span className={classes.resultAction} />
                    <span className={classes.resultValue}>{variantCode}</span>
                    <span className={classes.resultAction} />
                  </span>
                </div>
              )}
            </Typography>
            {loadLoginInfoInProgress && (
              <Typography
                variant="body1"
                classes={{ root: classes.TypographyCaption }}
                id="alert-dialog-description"
              >
                {t('saveConfiguration.loadLoginInfoInProgressText')}
              </Typography>
            )}
          </React.Fragment>
        )}
      </DialogContent>
      <DialogActions>
        {!workInProgress && code && (
          <MainButton onClick={onOkClick} variant="contained" size="medium" className={classes.activeButton}>
            {t('saveConfiguration.okText')}
          </MainButton>
        )}
      </DialogActions>
    </CustomDialog>
  );
};

export default withWidth()(withStyles(styles, { name: 'SaveConfiguration' })(CustomSaveConfiguration));
